import type { Shadows } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import type { ThemeColorScheme } from '../types';
import { common, grey } from './palette';

/**
 * Creates an array of shadow values with varying intensities for use in UI components.
 *
 * The function generates a series of CSS box-shadow values with increasing elevations.
 * Each shadow consists of three components with different opacity levels of the same color.
 *
 * @param colorChannel - The CSS variable or color value to be used as the shadow color base
 * @returns An array of shadow values where index 0 is 'none' and subsequent indices contain
 *          increasingly pronounced shadow effects (from elevation 1 to 24)
 *
 * @example
 * // Using with a CSS variable
 * const shadows = createShadows('var(--shadow-color)');
 *
 * @example
 * // Applying a shadow to a styled component
 * const Card = styled('div')(({ theme }) => ({
 *   boxShadow: theme.shadows[3], // Using elevation 3 shadow
 * }));
 */
function createShadows(colorChannel: string): Shadows {
    const color1 = varAlpha(colorChannel, 0.2);
    const color2 = varAlpha(colorChannel, 0.14);
    const color3 = varAlpha(colorChannel, 0.12);

    return [
        'none',
        `0px 2px 1px -1px ${color1},0px 1px 1px 0px ${color2},0px 1px 3px 0px ${color3}`,
        `0px 3px 1px -2px ${color1},0px 2px 2px 0px ${color2},0px 1px 5px 0px ${color3}`,
        `0px 3px 3px -2px ${color1},0px 3px 4px 0px ${color2},0px 1px 8px 0px ${color3}`,
        `0px 2px 4px -1px ${color1},0px 4px 5px 0px ${color2},0px 1px 10px 0px ${color3}`,
        `0px 3px 5px -1px ${color1},0px 5px 8px 0px ${color2},0px 1px 14px 0px ${color3}`,
        `0px 3px 5px -1px ${color1},0px 6px 10px 0px ${color2},0px 1px 18px 0px ${color3}`,
        `0px 4px 5px -2px ${color1},0px 7px 10px 1px ${color2},0px 2px 16px 1px ${color3}`,
        `0px 5px 5px -3px ${color1},0px 8px 10px 1px ${color2},0px 3px 14px 2px ${color3}`,
        `0px 5px 6px -3px ${color1},0px 9px 12px 1px ${color2},0px 3px 16px 2px ${color3}`,
        `0px 6px 6px -3px ${color1},0px 10px 14px 1px ${color2},0px 4px 18px 3px ${color3}`,
        `0px 6px 7px -4px ${color1},0px 11px 15px 1px ${color2},0px 4px 20px 3px ${color3}`,
        `0px 7px 8px -4px ${color1},0px 12px 17px 2px ${color2},0px 5px 22px 4px ${color3}`,
        `0px 7px 8px -4px ${color1},0px 13px 19px 2px ${color2},0px 5px 24px 4px ${color3}`,
        `0px 7px 9px -4px ${color1},0px 14px 21px 2px ${color2},0px 5px 26px 4px ${color3}`,
        `0px 8px 9px -5px ${color1},0px 15px 22px 2px ${color2},0px 6px 28px 5px ${color3}`,
        `0px 8px 10px -5px ${color1},0px 16px 24px 2px ${color2},0px 6px 30px 5px ${color3}`,
        `0px 8px 11px -5px ${color1},0px 17px 26px 2px ${color2},0px 6px 32px 5px ${color3}`,
        `0px 9px 11px -5px ${color1},0px 18px 28px 2px ${color2},0px 7px 34px 6px ${color3}`,
        `0px 9px 12px -6px ${color1},0px 19px 29px 2px ${color2},0px 7px 36px 6px ${color3}`,
        `0px 10px 13px -6px ${color1},0px 20px 31px 3px ${color2},0px 8px 38px 7px ${color3}`,
        `0px 10px 13px -6px ${color1},0px 21px 33px 3px ${color2},0px 8px 40px 7px ${color3}`,
        `0px 10px 14px -6px ${color1},0px 22px 35px 3px ${color2},0px 8px 42px 7px ${color3}`,
        `0px 11px 14px -7px ${color1},0px 23px 36px 3px ${color2},0px 9px 44px 8px ${color3}`,
        `0px 11px 15px -7px ${color1},0px 24px 38px 3px ${color2},0px 9px 46px 8px ${color3}`,
    ];
}

export const shadows: Record<ThemeColorScheme, Shadows> = {
    light: createShadows(grey['500Channel']),
    dark: createShadows(common.blackChannel),
};
